import AppShell from "@/components/AppShell";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  User,
  Phone,
  Mail,
  Edit2,
  Home,
  Save,
  X,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  classLabelFromKey,
  getSessionUser,
  getStudentDeleteRequestForUser,
  getStudentRecordForSession,
  getTeacherAssignedClass,
  saveStudentDeleteRequest,
  updateAccountProfile,
} from "@/lib/eisStore";
import {
  getAdminProfile,
  isSupabaseConfigured,
  updateAdminContact,
} from "@/lib/supabaseOps";

function Field({ Icon, label, value, testId }) {
  return (
    <div
      data-testid={testId}
      className="flex items-start gap-3 p-4 rounded-xl border border-slate-200 bg-white"
    >
      <div
        className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: "#EEF0FA" }}
      >
        <Icon size={18} style={{ color: "#0b1f7a" }} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs uppercase tracking-wide text-slate-500 font-semibold">
          {label}
        </p>
        <p className="text-sm md:text-base text-slate-800 font-medium mt-0.5 break-words">
          {value && value.length ? value : <span className="text-slate-400 italic">Not set</span>}
        </p>
      </div>
    </div>
  );
}

function TeacherProfile({ user }) {
  const assignedClass = getTeacherAssignedClass(user);

  return (
    <div data-testid="profile-page" className="space-y-6">
      <Card>
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6">
          <div
            className="h-32 w-32 md:h-36 md:w-36 rounded-full flex items-center justify-center flex-shrink-0 shadow-md border-4 border-white"
            style={{ backgroundColor: "#EEF0FA" }}
          >
            <User size={72} style={{ color: "#0b1f7a" }} strokeWidth={1.5} />
          </div>
          <div className="flex-1 text-center md:text-left min-w-0">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 break-words">
              {user.name || "Teacher"}
            </h2>
            <p className="text-slate-500 mt-1 capitalize">
              teacher · {assignedClass ? classLabelFromKey(assignedClass.key) : "Class not set"}
            </p>
            <p className="text-sm text-slate-500 mt-2 break-words">{user.email}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6">
        <Card>
          <CardContent className="p-6 space-y-3">
            <Field Icon={User} label="Name" value={user.name} testId="field-teacher-name" />
            <Field Icon={Mail} label="Email" value={user.email} testId="field-email" />
            <Field
              Icon={User}
              label="Assigned Class"
              value={assignedClass ? classLabelFromKey(assignedClass.key) : "Not set"}
              testId="field-teacher-class"
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AdminProfile({ user }) {
  const [isEditing, setIsEditing] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(Boolean(isSupabaseConfigured));
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    phone: user.phone || "",
    address: user.address || "",
  });

  useEffect(() => {
    let cancelled = false;

    const loadAdminProfile = async () => {
      if (!isSupabaseConfigured || user.role !== "admin") {
        setLoadingProfile(false);
        setForm({
          name: user.name || "",
          email: user.email || "",
          phone: user.phone || "",
          address: user.address || "",
        });
        return;
      }

      try {
        const adminProfile = await getAdminProfile();
        if (cancelled) return;
        setForm({
          name: adminProfile?.name || user.name || "",
          email: adminProfile?.email || user.email || "",
          phone: adminProfile?.phone_number || adminProfile?.phone || user.phone || "",
          address: adminProfile?.address || user.address || "",
        });
      } catch (error) {
        console.error(error);
        if (!cancelled) {
          setForm({
            name: user.name || "",
            email: user.email || "",
            phone: user.phone || "",
            address: user.address || "",
          });
        }
      } finally {
        if (!cancelled) setLoadingProfile(false);
      }
    };

    loadAdminProfile();
    return () => {
      cancelled = true;
    };
  }, [user.address, user.email, user.name, user.phone, user.role]);

  const save = async () => {
    if (!form.phone.trim() && !form.address.trim()) {
      toast.error("Please enter a phone number or address to update");
      return;
    }

    try {
      if (isSupabaseConfigured && user.role === "admin") {
        await updateAdminContact({
          phoneNumber: form.phone.trim(),
          address: form.address.trim(),
        });
      }
      updateAccountProfile(user.email, user.role, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
      });
      toast.success("Admin details updated");
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      toast.error(error?.message || "Unable to update admin details");
    }
  };

  return (
    <div data-testid="profile-page" className="space-y-6">
      <Card>
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6">
          <div
            className="h-32 w-32 md:h-36 md:w-36 rounded-full flex items-center justify-center flex-shrink-0 shadow-md border-4 border-white"
            style={{ backgroundColor: "#EEF0FA" }}
          >
            <User size={72} style={{ color: "#0b1f7a" }} strokeWidth={1.5} />
          </div>
          <div className="flex-1 text-center md:text-left min-w-0">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 break-words">
              {form.name || "Admin"}
            </h2>
            <p className="text-slate-500 mt-1 capitalize">
              admin account
            </p>
            <p className="text-sm text-slate-500 mt-2 break-words">{form.email}</p>
            {loadingProfile && (
              <p className="text-xs text-slate-400 mt-2">Loading admin profile...</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
              <Button
                data-testid="profile-edit-btn"
                onClick={() => setIsEditing((current) => !current)}
                className="rounded-full text-white"
                style={{ backgroundColor: "#0b1f7a" }}
              >
                <Edit2 size={16} className="mr-2" />
                {isEditing ? "Close Edit" : "Edit Details"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="profile-fields">
        <Field Icon={User} label="Name" value={form.name} testId="field-admin-name" />
        <Field Icon={Mail} label="Email" value={form.email} testId="field-admin-email" />
        <Field Icon={Phone} label="Ph. No." value={form.phone} testId="field-phone" />
        <Field Icon={Home} label="Address" value={form.address} testId="field-address" />
      </div>

      {isEditing && (
        <Card>
              <CardContent className="p-6 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-semibold text-slate-700">Name</p>
                <Input value={form.name} className="mt-1" disabled />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Email</p>
                <Input type="email" value={form.email} className="mt-1" disabled />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Phone Number</p>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
                  className="mt-1"
                  placeholder="Phone number"
                />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-700">Address</p>
                <Input
                  value={form.address}
                  onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
                  className="mt-1"
                  placeholder="Address"
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                onClick={() => {
                  setForm({
                    name: user.name || "",
                    email: user.email || "",
                    phone: user.phone || "",
                    address: user.address || "",
                  });
                  setIsEditing(false);
                }}
              >
                <X size={16} className="mr-2" />
                Cancel
              </Button>
              <Button
                type="button"
                className="rounded-full text-white"
                style={{ backgroundColor: "#0b1f7a" }}
                onClick={save}
              >
                <Save size={16} className="mr-2" />
                Save Details
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function StudentProfile({ user }) {
  const navigate = useNavigate();
  const studentRecord = getStudentRecordForSession(user) || {};
  const existingDeleteRequest = getStudentDeleteRequestForUser(user);
  const [deleteRequestSent, setDeleteRequestSent] = useState(Boolean(existingDeleteRequest));
  const profile = {
    ...user,
    ...studentRecord,
  };

  const startEdit = () => {
    navigate("/change-of-information");
  };

  const handleDeleteAccountRequest = () => {
    const request = saveStudentDeleteRequest({
      studentId: studentRecord.id || profile.studentId || profile.id || "",
      studentName: profile.studentName || profile.name || "Student",
      studentEmail: profile.email || "",
      standard: profile.standard || "",
      division: profile.division || "",
      classKey: profile.standard && profile.division ? `${profile.standard}__${profile.division}` : "",
    });
    if (request) {
      setDeleteRequestSent(true);
      toast.success("Your request has been recorded and your account will be deleted");
    }
  };

  return (
    <div data-testid="profile-page" className="space-y-6">
      <Card>
        <CardContent className="p-6 md:p-8 flex flex-col md:flex-row items-center md:items-start gap-6">
          <div
            data-testid="profile-avatar"
            className="h-32 w-32 md:h-36 md:w-36 rounded-full flex items-center justify-center flex-shrink-0 shadow-md border-4 border-white"
            style={{ backgroundColor: "#EEF0FA" }}
          >
            <User size={72} style={{ color: "#0b1f7a" }} strokeWidth={1.5} />
          </div>
          <div className="flex-1 text-center md:text-left min-w-0">
            <h2
              data-testid="profile-name"
              className="text-2xl md:text-3xl font-bold text-slate-800 break-words"
            >
              {profile.studentName || profile.name || "Student"}
            </h2>
            <p className="text-slate-500 mt-1 capitalize">
              {profile.role || "student"} · {profile.standard ? `${profile.standard} ${profile.division || ""}` : "—"}
            </p>
            <div className="mt-4 flex flex-wrap gap-2 justify-center md:justify-start">
              <Button
                data-testid="profile-edit-btn"
                onClick={startEdit}
                className="rounded-full text-white"
                style={{ backgroundColor: "#0b1f7a" }}
              >
                <Edit2 size={16} className="mr-2" />
                Edit Info
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="profile-fields">
        <Field Icon={User} label="Name" value={profile.studentName || profile.name} testId="field-student-name" />
        <Field Icon={User} label="Mother Name" value={profile.motherName} testId="field-mother-name" />
        <Field Icon={User} label="Father Name" value={profile.fatherName} testId="field-father-name" />
        <Field Icon={Phone} label="Ph. No." value={profile.phone} testId="field-phone" />
        <Field Icon={Mail} label="Email" value={profile.email} testId="field-email" />
        <Field Icon={User} label="Std" value={profile.standard} testId="field-standard" />
        <Field Icon={User} label="Div" value={profile.division} testId="field-division" />
      </div>

      <Card>
        <CardContent className="p-6 space-y-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">Delete my account</p>
            <p className="text-sm text-slate-500 mt-1">
              This will notify the admin with your name, class, and division so your request can be processed.
            </p>
          </div>
          {deleteRequestSent ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-800">
              Your request has been recorded and your account will be deleted
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-3 justify-end">
            <Button
              type="button"
              className="rounded-full text-white"
              style={{ backgroundColor: "#dc2626" }}
              onClick={handleDeleteAccountRequest}
              disabled={deleteRequestSent}
            >
              <Trash2 size={16} className="mr-2" />
              Delete my account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function Profile() {
  const stored = getSessionUser() || {};
  const user = stored;

  if (user.role === "teacher") {
    return (
      <AppShell>
        <TeacherProfile user={user} />
      </AppShell>
    );
  }

  if (user.role === "admin" || user.role === "management") {
    return (
      <AppShell>
        <AdminProfile user={user} />
      </AppShell>
    );
  }

  return (
    <AppShell>
      <StudentProfile user={user} />
    </AppShell>
  );
}
